/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Text } from '@tishman/components';
import { format } from 'date-fns';
import invariant from 'invariant';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  selectFlow,
  selectHasPerformances,
  selectShopCartDateTime,
  selectWizardCurrentStep,
  useAppSelector,
} from '../../store';
import { ContinueButton, Step } from '../../components/BuyTickets';
import { BuyTicketsLayoutContext } from '../../layouts/BuyTicketsLayout';
import { Flow } from '../../services/viva/constants';

import { BuyTicketsCalendar } from './BuyTicketsCalendar';
import { useContinueButtonValidation } from './BuyTicketsTicketStepBlock';

import type { BuyTicketsVariantPageData } from '../../components/BuyTickets';

export const BuyTicketsDateStepBlock = ({
  data,
  stepNumber,
  isSmallerFontSize,
  width = 'default',
}: {
  stepNumber: number;
  data: BuyTicketsVariantPageData;
  isSmallerFontSize?: boolean;
  width?: 'wide' | 'default';
}): React.JSX.Element => {
  const { page } = data;
  const ref = useRef<HTMLDivElement>(null);

  invariant(page, 'Expected valid buy tickets page json');

  const currentStep = useAppSelector(selectWizardCurrentStep);
  const dateTime = useAppSelector(selectShopCartDateTime);
  const flow = useAppSelector(selectFlow);
  const hasPerformances = useAppSelector(selectHasPerformances);

  const { isLoading } = useContext(BuyTicketsLayoutContext);

  // if ticket selection is valid
  const { isSelectionValid } = useContinueButtonValidation();
  const [userSelectedDate, setUserSelectedDate] = useState<boolean>(false);

  // Once user selects a date, show the step description
  useEffect(() => {
    if (userSelectedDate) return;

    if (stepNumber === currentStep) {
      setUserSelectedDate(true);
    }
  }, [userSelectedDate, setUserSelectedDate, stepNumber, currentStep]);

  const stepData = useMemo(() => {
    if (!('dateStep' in page) || !page.dateStep?.title)
      throw new Error('Expected buy tickets page title');
    return {
      title: page.dateStep.title,
      description: page.dateStep.description,
    };
  }, [page]);

  const Summary = (): JSX.Element => (
    <Text data-date={dateTime} data-testid="date-step-selected-date">
      {format(dateTime, 'MMMM dd, yyyy')}
    </Text>
  );

  return (
    <Step
      data-section="BuyTicketsDateStep"
      isSmallerFontSize={isSmallerFontSize}
      ref={ref}
      showLoadingModal={currentStep === stepNumber && isLoading}
      stepIsValid={isSelectionValid}
      stepNumber={stepNumber}
      summary={userSelectedDate ? <Summary /> : undefined}
      title={stepData.title}
      width={width}
    >
      <BuyTicketsCalendar
        continueButton={
          <ContinueButton
            disabled={
              flow !== Flow.RC_ROCK_PASS &&
              flow !== Flow.TOR_EXPRESS &&
              (!hasPerformances || isLoading)
            }
            sx={{
              width: '100%',
            }}
          />
        }
        stepNumber={stepNumber}
        twoColumn
      />
    </Step>
  );
};
